import { useState } from "react";
import type { Session } from "@/services/SessionsService";
import { SessionsService } from "@/services/SessionsService";

interface SessionCardProps {
  session: Session;
  onClick: () => void;
}

const getPlatformInfo = (device?: { brand?: string; model?: string }): { label: string; color: string; icon: JSX.Element } => {
  const brand = device?.brand?.toLowerCase() || "";
  const model = device?.model?.toLowerCase() || "";

  if (brand.includes("samsung") || model.includes("wear") || model.includes("sm-r")) {
    return {
      label: "Wear OS",
      color: "bg-green-100 text-green-700",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    };
  }

  if (brand.includes("apple") && model.includes("watch")) {
    return {
      label: "Apple Watch",
      color: "bg-gray-100 text-gray-700",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    };
  }

  if (brand.includes("apple") || model.includes("iphone") || model.includes("ipad")) {
    return {
      label: "iOS",
      color: "bg-gray-100 text-gray-700",
      icon: (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      ),
    };
  }

  // Default to Android
  return {
    label: "Android",
    color: "bg-green-100 text-green-700",
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10z" />
      </svg>
    ),
  };
};

export const SessionCard: React.FC<SessionCardProps> = ({ session, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const platformInfo = getPlatformInfo(session.device);

  const hasAnyPR = session.heightpr || session.airtimepr || session.distancepr || session.speedpr;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("SessionCard clicked, session ID:", session.ID);
    onClick();
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
    >
      {/* Session Image (if present) */}
      {session.sessionpicid && !imageError && (
        <div className="relative h-48 bg-gray-100">
          <img
            src={SessionsService.getSessionImageUrl(session.sessionpicid)}
            alt="Session"
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
          {/* PR Trophy Badge */}
          {hasAnyPR && (
            <div className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
              </svg>
            </div>
          )}
        </div>
      )}

      {/* Card Content */}
      <div className="p-4">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-900 font-medium">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {SessionsService.formatDate(session.startepoch)}
            </div>
            {session.spot?.name && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {session.spot.name}
              </div>
            )}
          </div>

          {/* Platform & Device Badge */}
          <div className="text-right">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${platformInfo.color}`}>
              {platformInfo.icon}
              {platformInfo.label}
            </span>
            {session.device?.model && (
              <p className="text-xs text-gray-400 mt-1">{session.device.model}</p>
            )}
          </div>
        </div>

        {/* Time on Water */}
        {session.timeonwater && session.timeonwater > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {SessionsService.formatDuration(session.timeonwater)}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2 py-3 border-t border-b border-gray-100">
          <StatItem
            label="Max Height"
            value={session.maxheight ? `${session.maxheight.toFixed(1)}m` : "-"}
            isPR={session.heightpr}
          />
          <StatItem
            label="Max Airtime"
            value={session.maxairtime ? `${session.maxairtime.toFixed(1)}s` : "-"}
            isPR={session.airtimepr}
          />
          <StatItem
            label="Max Distance"
            value={session.maxdistance ? `${session.maxdistance.toFixed(1)}m` : "-"}
            isPR={session.distancepr}
          />
          <StatItem
            label="Max Speed"
            value={session.maxspeed ? `${Math.round(session.maxspeed)} km/h` : "-"}
            isPR={session.speedpr}
          />
        </div>

        {/* Footer Row */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {/* Comments */}
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {session.commentscount || 0}
            </span>
            {/* Likes */}
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill={session.likedByCaller ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {session.likescount || 0}
            </span>
          </div>

          {/* Visibility Badge */}
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
            session.visiblesession
              ? "bg-green-100 text-green-700"
              : "bg-orange-100 text-orange-700"
          }`}>
            {session.visiblesession ? (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Visible
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
                Hidden
              </>
            )}
          </span>
        </div>

        {/* Session Remark/Notes */}
        {session.remark && (
          <p className="mt-3 text-sm text-gray-600 italic line-clamp-2">
            "{session.remark}"
          </p>
        )}
      </div>
    </div>
  );
};

// Stat Item Component
const StatItem: React.FC<{ label: string; value: string; isPR?: boolean }> = ({ label, value, isPR }) => (
  <div className="text-center">
    {isPR && (
      <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded mb-1">
        PR
      </span>
    )}
    <p className={`text-sm font-semibold ${isPR ? "text-cyan-600" : "text-gray-900"}`}>
      {value}
    </p>
    <p className="text-[10px] text-gray-500 mt-0.5">{label}</p>
  </div>
);
